// src/utils/printReceipt.js — v3
// Fixes: shorter safe width (no wrapping), GSTIN/FSSAI single-line,
// no "Table:" label confusion, manual discount line, GST-after-discount display

import { fmt } from './money';
import { cleanAddons } from './addons';

const WIDTH = 32; // safe for both 58mm and 80mm printers
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function pad(str, len) { str = String(str); return str.length >= len ? str.slice(0, len) : str + ' '.repeat(len - str.length); }
function padLeft(str, len) { str = String(str); return str.length >= len ? str.slice(0, len) : ' '.repeat(len - str.length) + str; }
function center(str) { str = String(str); const space = Math.max(0, WIDTH - str.length); return ' '.repeat(Math.floor(space / 2)) + str; }
function line(ch = '-') { return ch.repeat(WIDTH); }
function twoCol(left, right) {
  left = String(left); right = String(right);
  const space = Math.max(1, WIDTH - left.length - right.length);
  return left + ' '.repeat(space) + right;
}
function wrapItemName(name, maxLen) {
  if (name.length <= maxLen) return [name];
  const words = name.split(' ');
  const lines = []; let cur = '';
  words.forEach(w => {
    if ((cur + ' ' + w).trim().length > maxLen) { lines.push(cur.trim()); cur = w; }
    else cur = (cur + ' ' + w).trim();
  });
  if (cur) lines.push(cur.trim());
  return lines;
}

export function printReceipt({ bill, settings, loyaltyData, copies = 1 }) {
  const {
    restaurantName  = 'VIP PIZZA',
    address         = '',
    phone           = '',
    gstNumber       = '',
    fssaiNumber     = '',
    receiptTagline  = 'The Taste of Excellence',
    receiptFooter   = 'Thank you for dining with us!',
    feedbackPhone   = '',
  } = settings || {};

  const {
    billNo, orderLabel, // e.g. "T1", "Takeout", "Delivery"
    items        = [],
    subtotal     = 0,
    manualDiscountPercent = 0,
    manualDiscountAmt = 0,
    cgst = 0, sgst = 0, roundOff = 0,
    loyaltyDiscount = 0,
    pointsRedeemed  = 0,
    total        = 0,
    paymentMode  = 'cash',
    customerName,
    customerPhone,
    offer,
    createdAt,
  } = bill;

  const now  = createdAt?.toDate?.() || new Date();
  const date = now.toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' });
  const time = now.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });

  const ptsEarned = loyaltyData?.pointsEarned || 0;
  const totalPts  = loyaltyData?.totalPoints   || 0;
  const tier      = loyaltyData?.tier          || '';

  const lines = [];
  const add = (s = '') => lines.push(s);

  add(center(restaurantName));
  if (receiptTagline) add(center(receiptTagline));
  add(line('='));
  if (address) add(center(address));
  if (phone)   add(center('Ph: ' + phone));
  add(center('order.vippizza.in'));
  add(line('='));
  // Single-line GSTIN/FSSAI — no column padding, just plain text
  if (gstNumber)   add('GSTIN: ' + gstNumber);
  if (fssaiNumber) add('FSSAI: ' + fssaiNumber);
  if (gstNumber || fssaiNumber) add(line('-'));

  add('Bill No: ' + billNo);
  add(orderLabel); // "T1" or "Takeout" or "Delivery" — no confusing "Table:" prefix
  add(date + '  ' + time);
  add(line('-'));

  if (customerName)  add('Customer: ' + customerName);
  if (customerPhone) add('Phone: ' + customerPhone);
  if (customerName || customerPhone) add(line('-'));

  // group identical lines into one row with a quantity (e.g. "2 x Margherita")
  const groups = new Map();
  items.forEach(item => {
    const addons = cleanAddons(item.addons);
    const key = [item.name, item.price, item.isFree ? 1 : 0, addons.join(',')].join('|');
    if (!groups.has(key)) groups.set(key, { item, addons, qty: 0 });
    groups.get(key).qty += 1;
  });
  groups.forEach(({ item, addons, qty }) => {
    const lineTotal = (Number(item.price) || 0) * qty;
    const priceStr = (item.isFree && lineTotal === 0) ? 'Rs.0' : 'Rs.' + fmt(lineTotal);
    const title = (qty > 1 ? `${qty} x ` : '') + (item.name || 'Item') + (item.isFree ? ' [FREE]' : '');
    wrapItemName(title, 20).forEach((nl, idx) => {
      if (idx === 0) add(pad(nl, 20) + padLeft(priceStr, 12));
      else add(pad(nl, 20) + padLeft('', 12));
    });
    addons.forEach(a => add('  +' + a));
  });

  add(line('-'));
  add(twoCol('Subtotal', 'Rs.' + fmt(subtotal)));
  if (manualDiscountAmt > 0) {
    add(twoCol(`Discount ${manualDiscountPercent}%`, '-Rs.' + fmt(manualDiscountAmt)));
  }
  if (loyaltyDiscount > 0) {
    add(twoCol('Points Redeemed', '-Rs.' + fmt(loyaltyDiscount)));
  }
  add(twoCol('CGST 2.5%', 'Rs.' + fmt(cgst)));
  add(twoCol('SGST 2.5%', 'Rs.' + fmt(sgst)));
  if (Math.abs(roundOff) >= 0.005) add(twoCol('Round off', (roundOff < 0 ? '-' : '+') + 'Rs.' + fmt(Math.abs(roundOff))));
  add(line('='));
  add(twoCol('TOTAL', 'Rs.' + fmt(total)));
  add(line('='));

  add('');
  add(twoCol('Payment:', paymentMode === 'cash' ? 'CASH' : 'ONLINE'));

  if (offer) { add(line('-')); add(center(offer + ' Applied!')); }

  if (ptsEarned > 0 || pointsRedeemed > 0) {
    add(line('-'));
    add(center('LOYALTY POINTS'));
    if (pointsRedeemed > 0) add(twoCol('Redeemed:', '-' + pointsRedeemed + ' pts'));
    if (ptsEarned > 0)      add(twoCol('Earned:', '+' + ptsEarned + ' pts'));
    add(twoCol('Balance:', totalPts + ' pts'));
    if (tier) add(center(tier + ' Member'));
    const nx = loyaltyData?.nextExpiry;
    if (nx && totalPts > 0) add(center(`${nx.pts} pts expire ${new Date(nx.at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`));
  }

  add(line('-'));
  add(center(receiptFooter));
  add(center('order.vippizza.in'));
  if (feedbackPhone) add(center('Feedback: ' + feedbackPhone));
  add(line('-'));
  add(center('** Computer generated **'));
  add('');

  const receiptText = esc(lines.join('\n'));
  const html = `
<!DOCTYPE html><html><head><meta charset="utf-8"><title>Bill ${esc(billNo)}</title>
<style>
  @page { margin: 0; size: 80mm auto; }
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',monospace; font-size:12px; width:78mm; padding:2mm; }
  pre { font-family:'Courier New',monospace; font-size:12px; white-space:pre-wrap; word-break:break-word; line-height:1.4; }
</style></head><body>
<pre>${receiptText}</pre>
<script>
  // one print job PER copy, so the thermal printer cuts after each bill instead of printing both on one slip
  window.onload = function () {
    var copies = ${Math.max(1, copies)}, done = 0;
    function printNext() {
      window.print();
      done++;
      if (done < copies) setTimeout(printNext, 1500);
      else setTimeout(function () { window.close(); }, 1500);
    }
    printNext();
  };
</script>
</body></html>`;

  const win = window.open('', '_blank', 'width=400,height=700,toolbar=no,menubar=no');
  if (!win) { alert('Please allow popups to print receipts'); return; }
  win.document.open(); win.document.write(html); win.document.close();
}